/**
 * 全局配置服务
 * 管理 BlueMap 配置和 Marker 样式配置的默认值
 */

// 默认 BlueMap 配置
const DEFAULT_BLUEMAP_CONFIG = {
  world: "world/rofc",
  dimension: "minecraft:overworld",
  name: "world",
  sorting: 0,
  startPos: {
    enabled: false,
    x: 500,
    z: -820
  },
  skyColor: "#7dabff",
  voidColor: "#000000",
  ambientLight: 1,
  removeCavesBelowY: 55,
  caveDetectionOceanFloor: -5,
  caveDetectionUsesBlockLight: false,
  renderBounds: {
    enabled: false,
    minX: -4000,
    maxX: 4000,
    minZ: -4000,
    maxZ: 4000,
    minY: 50,
    maxY: 100
  },
  minInhabitedTime: 0,
  renderEdges: true,
  saveHiresLayer: true,
  storage: "file",
  ignoreMissingLightData: true
}

// 默认 Marker 样式配置
const DEFAULT_MARKER_STYLES = {
  stations: {
    depthTest: { enabled: true, value: false },
    newTab: { enabled: true, value: false },
    lineWidth: { enabled: false, value: 2 },
    lineColor: { enabled: false, r: 255, g: 0, b: 0, a: 0.3 },
    fillColor: { enabled: false, r: 200, g: 0, b: 0, a: 0.2 },
    minDistance: { enabled: true, value: 10 },
    maxDistance: { enabled: true, value: 10000000 }
  },
  html: {
    styles: [
      { property: 'line-height', value: '1em' },
      { property: 'font-size', value: '1em' },
      { property: 'color', value: 'white' },
      { property: 'transform', value: 'translate(-50%, -50%)' }
    ],
    anchor: { enabled: true, x: 0, y: 0 },
    depthTest: { enabled: true, value: false },
    minDistance: { enabled: true, value: 30 },
    maxDistance: { enabled: true, value: 2000 }
  },
  // 建筑物文字标记 (HTML)
  buildingHtml: {
    styles: [
      { property: 'font-size', value: '16px' },
      { property: 'color', value: 'white' },
      { property: 'transform', value: 'translate(-50%, -50%)' },
      { property: 'background-color', value: 'rgba(0, 0, 0, 0.3)' },
      { property: 'padding', value: '8px 12px' },
      { property: 'border-radius', value: '8px' },
      { property: 'text-align', value: 'center' },
      { property: 'line-height', value: '1.2' }
    ],
    anchor: { enabled: true, x: 0, y: 0 },
    depthTest: { enabled: true, value: false },
    minDistance: { enabled: true, value: 30 },
    maxDistance: { enabled: true, value: 800 }
  },
  lines: {
    depthTest: { enabled: true, value: false },
    lineWidth: { enabled: false, value: 3 },
    minDistance: { enabled: true, value: 10 },
    maxDistance: { enabled: true, value: 10000000 }
  },
  buildings: {
    depthTest: { enabled: true, value: true },
    listed: { enabled: true, value: true },
    sorting: { enabled: false, value: 0 },
    minDistance: { enabled: true, value: 10 },
    maxDistance: { enabled: true, value: 10000000 }
  },
  // 行政区划 Shape
  regionShapes: {
    depthTest: { enabled: true, value: false },
    listed: { enabled: true, value: true },
    lineWidth: { enabled: false, value: 5 },
    fillAlpha: { enabled: true, value: 0.22 },
    minDistance: { enabled: true, value: 20 },
    maxDistance: { enabled: true, value: 10000000 }
  },
  // 行政区划文字标签 (HTML)
  regionLabels: {
    styles: [
      { property: 'font-size', value: '26px' },
      { property: 'font-weight', value: '700' },
      { property: 'color', value: 'rgba(0,0,0,0.82)' },
      { property: 'text-shadow', value: '0 2px 8px rgba(0,0,0,0.35)' },
      { property: 'transform', value: 'translate(-50%, -50%)' }
    ],
    anchor: { enabled: true, x: 0, y: 0 },
    depthTest: { enabled: true, value: false },
    minDistance: { enabled: true, value: 80 },
    maxDistance: { enabled: true, value: 5000 }
  }
}

class ConfigService {
  constructor() {
    this.blueMapConfig = { ...DEFAULT_BLUEMAP_CONFIG }
    this.markerStyles = { ...DEFAULT_MARKER_STYLES }
    this.loadConfigs()
  }

  /**
   * 加载保存的配置
   */
  loadConfigs() {
    try {
      // 加载 BlueMap 配置
      const savedBlueMapConfig = localStorage.getItem('rofc_bluemap_config')
      if (savedBlueMapConfig) {
        const parsed = JSON.parse(savedBlueMapConfig)
        this.blueMapConfig = { ...DEFAULT_BLUEMAP_CONFIG, ...parsed }
      }

      // 加载 Marker 样式配置
      const savedMarkerStyles = localStorage.getItem('rofc_marker_styles')
      if (savedMarkerStyles) {
        const parsed = JSON.parse(savedMarkerStyles)
        this.markerStyles = this.mergeMarkerStyles(DEFAULT_MARKER_STYLES, parsed)
      }
    } catch (error) {
      console.warn('加载配置失败，使用默认配置:', error)
    }
  }

  /**
   * 深度合并 Marker 样式配置
   */
  mergeMarkerStyles(defaultStyles, savedStyles) {
    const merged = JSON.parse(JSON.stringify(defaultStyles))
    
    if (savedStyles.stations) {
      Object.assign(merged.stations, savedStyles.stations)
    }
    if (savedStyles.html) {
      Object.assign(merged.html, savedStyles.html)
    }
    if (savedStyles.lines) {
      Object.assign(merged.lines, savedStyles.lines)
    }
    if (savedStyles.buildings) {
      Object.assign(merged.buildings, savedStyles.buildings)
    }
    if (savedStyles.buildingHtml) {
      Object.assign(merged.buildingHtml, savedStyles.buildingHtml)
    }
    if (savedStyles.regionShapes) {
      Object.assign(merged.regionShapes, savedStyles.regionShapes)
    }
    if (savedStyles.regionLabels) {
      Object.assign(merged.regionLabels, savedStyles.regionLabels)
    }

    // 强制更新填充颜色的透明度默认值
    if (merged.stations.fillColor && merged.stations.fillColor.a === 0.1) {
      merged.stations.fillColor.a = 0.2
    }

    return merged
  }

  /**
   * 保存 BlueMap 配置
   */
  saveBlueMapConfig(config) {
    try {
      this.blueMapConfig = { ...DEFAULT_BLUEMAP_CONFIG, ...config }
      localStorage.setItem('rofc_bluemap_config', JSON.stringify(this.blueMapConfig))
      return true
    } catch (error) {
      console.error('保存 BlueMap 配置失败:', error)
      return false
    }
  }

  /**
   * 保存 Marker 样式配置
   */
  saveMarkerStyles(styles) {
    try {
      this.markerStyles = this.mergeMarkerStyles(DEFAULT_MARKER_STYLES, styles)
      localStorage.setItem('rofc_marker_styles', JSON.stringify(this.markerStyles))
      return true
    } catch (error) {
      console.error('保存 Marker 样式配置失败:', error)
      return false
    }
  }

  /**
   * 获取当前 BlueMap 配置
   */
  getBlueMapConfig() {
    return { ...this.blueMapConfig }
  }

  /**
   * 获取当前 Marker 样式配置
   */
  getMarkerStyles() {
    return { ...this.markerStyles }
  }

  /**
   * 获取用于 API 调用的配置对象
   */
  getApiConfig() {
    return {
      bluemap_config: this.getBlueMapConfig(),
      marker_styles: this.getMarkerStyles()
    }
  }

  /**
   * 重置为默认配置
   */
  resetToDefaults() {
    this.blueMapConfig = { ...DEFAULT_BLUEMAP_CONFIG }
    this.markerStyles = { ...DEFAULT_MARKER_STYLES }
    localStorage.removeItem('rofc_bluemap_config')
    localStorage.removeItem('rofc_marker_styles')
  }

  /**
   * 检查是否有保存的配置
   */
  hasSavedConfig() {
    return localStorage.getItem('rofc_bluemap_config') !== null || 
           localStorage.getItem('rofc_marker_styles') !== null
  }
}

// 创建单例实例
const configService = new ConfigService()

export default configService
